
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Users, Pencil, Trash2 } from 'lucide-react';
import { RoleWithPermissions } from '@/types/userRole';

interface RolesListProps {
  roles: RoleWithPermissions[];
  isLoading: boolean;
  onEdit: (role: RoleWithPermissions) => void;
  onDelete: (role: RoleWithPermissions) => void;
}

const RolesList: React.FC<RolesListProps> = ({
  roles,
  isLoading,
  onEdit,
  onDelete
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div className="text-center py-8">
        <Shield className="h-12 w-12 mx-auto text-gray-400" />
        <h3 className="mt-4 text-lg font-medium">No roles defined</h3>
        <p className="text-sm text-gray-500 mt-1">
          Create your first role to start managing permissions
        </p>
      </div>
    );
  }

  // Ensure order: Master Admin, Reseller, Administrator, Manager, User, any others last
  const sortPriority = (role: RoleWithPermissions) => {
    const order = [
      'Master Admin',
      'Reseller',
      'Administrator',
      'Manager',
      'User'
    ];
    const idx = order.findIndex(label => role.name.toLowerCase().includes(label.toLowerCase()));
    return idx === -1 ? order.length : idx;
  };
  const sortedRoles = roles.slice().sort((a, b) => sortPriority(a) - sortPriority(b));

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Role Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Users</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRoles.map((role) => (
            <TableRow key={role.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  {role.name}
                  {role.isDefault && (
                    <Badge variant="outline" className="ml-2">Default</Badge>
                  )}
                  {role.isSystem && (
                    <Badge variant="secondary" className="ml-2">System</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>{role.description}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span>{role.userCount}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {role.permissions.length > 0 ? (
                    <>
                      <Badge variant="outline" className="bg-gray-50">
                        {role.permissions.length} permissions
                      </Badge>
                    </>
                  ) : (
                    <Badge variant="outline" className="text-red-500 border-red-200 bg-red-50">
                      No permissions
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(role)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(role)}
                    disabled={role.isSystem || role.userCount > 0}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default RolesList;
